import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { WebService } from './web.service';

// =====================================================================
// PaymentsDataService — Scope 7.2 Payment Management (Accounting → Customer Payments)
// ---------------------------------------------------------------------
// Thin wrapper over routes/payments.js (mounted at /api/v1/payments).
// Same pattern as SwapDataService:
//   - WebService.getWebCall picks GET vs POST by whether `body` is passed
//   - every write sends ActionByGUID from localStorage('GUID')
//   - dates go out as 'YYYY-MM-DD' strings (the route never turns them into JS Dates)
// =====================================================================

/** One row of the Order Payment Status grid (get_order_payment_status) */
export interface OrderPaymentStatusRow {
  OrderID: number;
  CompanyID: number;
  CompanyName: string;
  SaleDate: string;
  PickupDate: string | null;
  DatePaid: string | null;
  InvoiceDate: string | null;
  NetDays: number;
  TermsText: string;
  OrderPayMethodName: string | null;
  DueDate: string | null;
  DaysLate: number | null;
  OrderTotal: number;
  SettledPaid: number;
  PendingPaid: number;
  BalanceDue: number;
  LeftToProcess: number;
  PaymentCount: number;
  ProcessedDate: string | null;
  PostedDate: string | null;
  WaitingToPost: number | null;
  WaitingException: number | null;
  EnteredWaiting: number | null;
  BankID: number | null;
  PaymentDetailTypeID: number | null;
  Charged: boolean;
  NextStep: 'PROCESS' | 'POST' | 'PAID';
  BankName: string | null;
  DetailTypeName: string | null;
}

export interface OrderPaymentStatusTotals {
  TotalRows: number;
  TotalBalanceDue: number;
  OverdueCount: number;
  OverdueAmount: number;
  ToProcessCount: number;
  ToPostCount: number;
}

export interface OrderPaymentStatusQuery {
  UnpaidOnly?: boolean;
  Search?: string | null;
  CompanyID?: number | null;
  MonthsBack?: number;
  OverdueOnly?: boolean;
  SortBy?: 'OrderID' | 'DueDate' | 'DaysLate' | 'Customer' | 'SaleDate' | 'Amount';
  SortDesc?: boolean;
  PageNumber?: number;
  PageSize?: number;
}

/** Body for POST /orders/process (Process button / Partial %) */
export interface ProcessOrderPaymentPayload {
  OrderID: number;
  BankID: number;
  PaymentDetailTypeID: number;
  Amount?: number | null;          // null = everything left on the order
  ReceivedDate?: string | null;    // 'YYYY-MM-DD'
  PostNow?: boolean;               // Partial "Post Payment"
  DepositDate?: string | null;     // 'YYYY-MM-DD', default today
}

export interface PaymentOptions {
  Banks: { BankID: number; BankName: string; BankCode: string; CashAccountID: number | null; IsActive: boolean }[];
  DetailTypes: {
    PaymentDetailTypeID: number; DetailTypeName: string; PaymentMethodID: number | null; MethodName: string | null;
    SameDayProcessing: boolean; IsWithdrawal: boolean; PostsToLedger: boolean; IsActive: boolean
  }[];
  Methods: { PaymentMethodID: number; MethodName: string }[];
  ExceptionCodes: { ExceptionCode: string; ExceptionName: string; IsSystem?: boolean }[];
}

/** Receipt tabs (get_payment_queue) — one row per receipt (dbo.payment) */
export type ReceiptStage = 'OPEN' | 'ENTERED' | 'PROCESSING' | 'POSTED' | 'EXCEPTIONS' | 'UNAPPLIED' | 'ALL';

export interface ReceiptQueueQuery {
  Stage: ReceiptStage;
  CompanyID?: number | null;
  OrderID?: number | null;
  BankID?: number | null;
  PaymentMethodID?: number | null;
  PaymentDetailTypeID?: number | null;
  ReceivedFrom?: string | null;   // 'YYYY-MM-DD'
  ReceivedTo?: string | null;
  DepositFrom?: string | null;
  DepositTo?: string | null;
  UnappliedOnly?: boolean;
  ExceptionCode?: string | null;
  OwnerGUID?: string | null;
  IncludeStale?: boolean;
  Search?: string | null;
  PageNumber?: number;
  PageSize?: number;            // max 500
}

export interface ReceiptQueueRow {
  ChargeLogID: number;
  CompanyID: number | null;
  CompanyName: string | null;
  OrderID: number | null;
  ReceivedDate: string | null;
  ProcessingDate: string | null;
  DepositDate: string | null;
  PostingDate: string | null;
  BankID: number | null;
  BankName: string | null;
  PaymentDetailTypeID: number | null;
  DetailTypeName: string | null;
  PaymentMethodID: number | null;
  MethodName: string | null;
  GrossAmount: number;
  CustomerFeeAmount: number | null;
  ProcessorFeeAmount: number | null;
  NetDepositAmount: number;
  ApplicableAmount: number;
  AppliedAmount: number;
  UnappliedAmount: number;
  AllocationCount: number;
  CheckNumber: string | null;
  MaskedReference: string | null;
  ProcessorName: string | null;
  ProcessorReference: string | null;
  ExceptionCode: string | null;
  EffectiveExceptionCode: string | null;
  ExceptionNote: string | null;
  OwnerGUID: string | null;
  OwnerName: string | null;
  NextAction: string | null;
  ReversalOfChargeLogID: number | null;
  ReversedByChargeLogID: number | null;
  Stage: 'ENTERED' | 'PROCESSING' | 'POSTED';
  IsStale: boolean;
  Notes: string | null;
  RowVer: string | number;
}

/** Tab badges / summary cards (3rd recordset of get_payment_queue; unposted receipts only) */
export interface ReceiptQueueSummary {
  EnteredCount: number;
  ProcessingCount: number;
  ProcessingAmount: number;
  OpenUnappliedCount: number;
  OpenUnappliedAmount: number;
  StaleCount: number;
  StaleAmount: number;
  ExceptionCount: number;
  ExceptionTabCount: number;
  NeedsReceiptCount: number;
}

/** Batch Process preview row (match_braintree_transactions) */
export interface BraintreeMatchRow {
  TransactionId: string;
  TxnType: string;
  BtStatus: string;
  Amount: number;
  OrderRef: string | null;
  OrderID: number | null;
  CreatedAt: string | null;
  ReceivedDate: string | null;
  DepositDate: string | null;
  Last4: string | null;
  Instrument: string | null;
  CompanyID: number | null;
  CompanyName: string | null;
  OpenAmount: number | null;
  ChargeLogID: number | null;
  ReceiptStage: string | null;
  ReceiptAmount: number | null;
  MatchedBy: 'BRAINTREE_ID' | 'ORDER_AMOUNT' | null;
  Result: 'READY' | 'CREATE' | 'POSTED' | 'EXCEPTION' | 'AMOUNT_DIFF' | 'ORDER_PAID' | 'NOT_SETTLED' | 'FAILED' | 'REFUND' | 'NO_MATCH';
  Action: 'POST' | 'CREATE' | 'LINK' | null;
  Message: string | null;
}

/** One ticked row after posting (post_braintree_batch) */
export interface BraintreePostResult {
  TransactionId: string;
  Action: string | null;
  Outcome: 'DONE' | 'SKIPPED' | 'ERROR';
  ChargeLogID: number | null;
  LedgerTransactionID: number | null;
  Message: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentsDataService {
  constructor(private web: WebService) { }

  /** Logged-in user's GUID (set at login in auth.service.ts) */
  get currentUserGUID(): string | null {
    return localStorage.getItem('GUID');
  }

  // Drops null/undefined/'' so the query string only carries real values.
  // Booleans go as 1/0 because the route reads them with toBit().
  private toParams(obj: Record<string, any>): { name: string, value: any }[] {
    return Object.entries(obj)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([name, value]) => ({ name, value: typeof value === 'boolean' ? (value ? 1 : 0) : value }));
  }

  /** Today as 'YYYY-MM-DD' in the user's own time zone (not UTC) */
  static today(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  /** API date ('2026-10-06T00:00:00.000Z') → '2026-10-06' for rms-date. SQL DATEs come back as UTC midnight,
   *  so the first 10 characters are the stored date (no time-zone shift). */
  static dateOnly(v: string | Date | null | undefined): string | null {
    if (!v) return null;
    const s = typeof v === 'string' ? v : v.toISOString();
    return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.substring(0, 10) : null;
  }

  // ---------------------------------------------------------------------
  // Lookups
  // ---------------------------------------------------------------------

  /** Banks, payment types, methods, exception codes */
  getOptions(): Observable<PaymentOptions> {
    return this.web.getWebCall('payments', 'options');
  }

  // ---------------------------------------------------------------------
  // Order Payment Status (legacy screen, one row per order) — Step B3
  // ---------------------------------------------------------------------

  /** The grid + totals */
  getOrderStatus(q: OrderPaymentStatusQuery): Observable<{ Rows: OrderPaymentStatusRow[]; Totals: OrderPaymentStatusTotals | null }> {
    return this.web.getWebCall('payments', 'orders/status', { params: this.toParams(q) });
  }

  /** "Amount" popup: { Summary, Payments } */
  getOrderPayments(orderId: number): Observable<{ Summary: any; Payments: any[] }> {
    return this.web.getWebCall('payments', `orders/${orderId}/payments`);
  }

  /** "Process" button / Partial %. Returns { ChargeLogID, Created, Posted, LedgerTransactionID, RowVer } */
  processOrder(payload: ProcessOrderPaymentPayload): Observable<any> {
    return this.web.getWebCall('payments', 'orders/process', {
      body: { ...payload, ActionByGUID: this.currentUserGUID }
    });
  }

  /** "Post" button. Returns one row per receipt posted */
  postOrder(orderId: number, depositDate?: string | null, bankId?: number | null): Observable<any[]> {
    return this.web.getWebCall('payments', 'orders/post', {
      body: { OrderID: orderId, DepositDate: depositDate ?? null, BankID: bankId ?? null, ActionByGUID: this.currentUserGUID }
    });
  }

  /** Click on the Processed date ("remove processing date") */
  unprocessOrder(orderId: number): Observable<any[]> {
    return this.web.getWebCall('payments', 'orders/unprocess', {
      body: { OrderID: orderId, ActionByGUID: this.currentUserGUID }
    });
  }

  // ---------------------------------------------------------------------
  // Receipt queue (Receipts / Exceptions / Needs receipt tabs) — Step D2 screens
  // ---------------------------------------------------------------------

  /** Receipt work queue: { Rows, TotalRows, Summary } */
  getQueue(q: ReceiptQueueQuery): Observable<{ Rows: ReceiptQueueRow[]; TotalRows: number; Summary: ReceiptQueueSummary | null }> {
    return this.web.getWebCall('payments', 'queue', { params: this.toParams(q as Record<string, any>) });
  }

  /** 5.4 payments with no accounting receipt yet */
  getNeedsReceipt(companyId?: number | null): Observable<any[]> {
    const params = this.toParams({ CompanyID: companyId });
    return this.web.getWebCall('payments', 'needs-receipt', params.length ? { params } : undefined);
  }

  /** Receipt pulldown: { Receipt, Allocations, Ledger, History, Related } */
  getReceipt(chargeLogId: number): Observable<{ Receipt: any; Allocations: any[]; Ledger: any[]; History: any[]; Related: any[] }> {
    return this.web.getWebCall('payments', `receipts/${chargeLogId}`);
  }

  /** Allocate tab: the customer's open orders */
  getAllocationOptions(chargeLogId: number): Observable<any[]> {
    return this.web.getWebCall('payments', `receipts/${chargeLogId}/allocation-options`);
  }

  saveReceipt(payload: any): Observable<{ ChargeLogID: number; RowVer: string; AlreadyExisted: boolean }> {
    return this.web.getWebCall('payments', 'receipts/save', { body: { ...payload, ActionByGUID: this.currentUserGUID } });
  }

  saveAllocations(chargeLogId: number, rowVer: string | number | null, allocations: { OrderID: number; Amount: number }[]): Observable<any> {
    return this.web.getWebCall('payments', 'receipts/allocations', {
      body: { ChargeLogID: chargeLogId, RowVer: rowVer, Allocations: allocations, ActionByGUID: this.currentUserGUID }
    });
  }

  markProcessing(chargeLogId: number, rowVer: string | number | null, processingDate?: string | null): Observable<any> {
    return this.web.getWebCall('payments', 'receipts/processing', {
      body: { ChargeLogID: chargeLogId, RowVer: rowVer, ProcessingDate: processingDate ?? null, ActionByGUID: this.currentUserGUID }
    });
  }

  postReceipt(chargeLogId: number, rowVer: string | number | null, depositDate: string, bankId: number | null): Observable<any> {
    return this.web.getWebCall('payments', 'receipts/post', {
      body: { ChargeLogID: chargeLogId, RowVer: rowVer, DepositDate: depositDate, BankID: bankId, ActionByGUID: this.currentUserGUID }
    });
  }

  returnReceipt(chargeLogId: number, rowVer: string | number | null, reason: string, returnDate?: string | null): Observable<any> {
    return this.web.getWebCall('payments', 'receipts/return', {
      body: { ChargeLogID: chargeLogId, RowVer: rowVer, Reason: reason, ReturnDate: returnDate ?? null, ActionByGUID: this.currentUserGUID }
    });
  }

  setFollowup(payload: {
    ChargeLogID: number; RowVer: string | number | null; ExceptionCode?: string | null;
    ExceptionNote?: string | null; OwnerGUID?: string | null; NextAction?: string | null
  }): Observable<any> {
    return this.web.getWebCall('payments', 'receipts/followup', { body: { ...payload, ActionByGUID: this.currentUserGUID } });
  }

  addNote(chargeLogId: number, noteText: string): Observable<any> {
    return this.web.getWebCall('payments', 'receipts/note', {
      body: { ChargeLogID: chargeLogId, NoteText: noteText, ActionByGUID: this.currentUserGUID }
    });
  }

  recordFromOrderPayment(payload: {
    OrderPaymentID: number; BankID: number; PaymentDetailTypeID?: number | null;
    ReceivedDate?: string | null; DepositDate?: string | null
  }): Observable<any> {
    return this.web.getWebCall('payments', 'receipts/from-order-payment', { body: { ...payload, ActionByGUID: this.currentUserGUID } });
  }

  // ---------------------------------------------------------------------
  // Braintree (routes/braintree.js, mounted at /api/v1/payments/braintree) — Step E
  // ---------------------------------------------------------------------

  /** Batch Process preview: coffee transactions created between From and To, matched to RMS */
  getBraintreeTransactions(from: string, to: string): Observable<{ Environment: string; Total: number; Truncated: boolean; Rows: BraintreeMatchRow[] }> {
    return this.web.getWebCall('payments', 'braintree/transactions', { params: this.toParams({ From: from, To: to }) });
  }

  /** Post the ticked transactions (the API re-reads them from Braintree first) */
  postBraintreeBatch(transactionIds: string[], bankId: number, paymentDetailTypeId: number): Observable<BraintreePostResult[]> {
    return this.web.getWebCall('payments', 'braintree/post', {
      body: { TransactionIds: transactionIds, BankID: bankId, PaymentDetailTypeID: paymentDetailTypeId, ActionByGUID: this.currentUserGUID }
    });
  }

  /** Status lookup for one Braintree transaction (7.2.28) */
  getBraintreeTransaction(transactionId: string): Observable<{ Environment: string; Transaction: any; Match: BraintreeMatchRow | null }> {
    return this.web.getWebCall('payments', `braintree/transactions/${encodeURIComponent(transactionId)}`);
  }
}
